<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-12">
    <div class="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 md:p-12">
      <!-- Icône -->
      <div class="flex justify-center mb-6">
        <div class="w-28 h-28 bg-[#04406E] rounded-full flex items-center justify-center">
          <svg class="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <!-- Titre -->
      <h1 class="text-center text-[#04406E] text-2xl md:text-3xl font-bold uppercase mb-4">
        SUIVEZ VOTRE SIGNALEMENT
      </h1>

      <!-- Description -->
      <p class="text-center text-gray-700 text-sm md:text-base mb-8 leading-relaxed">
        Entrez votre code confidentiel pour consulter l'état de votre signalement et échanger avec la Cour
      </p>

      <!-- Message d'erreur -->
      <div v-if="error" class="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
        <p class="text-red-800 text-sm">
          <span class="font-semibold">Erreur:</span> {{ error }}
        </p>
      </div>

      <!-- Formulaire -->
      <div class="mb-6">
        <label class="block text-gray-900 font-semibold text-sm mb-3">
          Code Confidentiel
        </label>
        <input
          type="text"
          placeholder="Ex: 7CE5-A442-EE0B"
          class="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg focus:border-[#04406E] focus:ring-2 focus:ring-[#04406E]/20 outline-none transition-all text-sm uppercase"
          :class="{ 'border-red-500': error }"
          v-model="confidentialCode"
          :disabled="isLoading"
          @keyup.enter="handleSubmit"
          @input="clearError"
        />
      </div>

      <!-- Bouton -->
      <button
        @click="handleSubmit"
        :disabled="isLoading || !confidentialCode.trim()"
        class="w-full bg-[#04406E] hover:bg-[#033a5a] text-white font-semibold py-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        <span v-if="!isLoading">Accéder au Signalement</span>
        <span v-else class="flex items-center gap-2">
          <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Chargement...
        </span>
      </button>

      <!-- Note d'information -->
      <div class="mt-8 bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
        <p class="text-gray-800 text-sm leading-relaxed">
          <span class="font-semibold">Note:</span> Le code confidentiel vous a été communiqué lors de la création de votre signalement. 
          Si vous l'avez perdu et n'êtes pas anonyme, contactez-nous à l'adresse: 
          <a href="mailto:signalement@courdescomptes.bj" class="text-blue-600 hover:text-blue-800 underline">
            signalement@courdescomptes.bj
          </a>
        </p>
      </div>

      <!-- Lien de création -->
      <p class="text-center mt-8 text-sm text-gray-600">
        Vous n'avez pas encore fait de signalement ? 
        <router-link 
          :to="{ name: 'faire-un-signalement' }" 
          class="text-[#16a34a] hover:text-[#15803d] font-semibold hover:underline ml-1"
        >
          Créer un signalement
        </router-link>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { getSignalementByCode, type Signalement, type ApiError } from '@/services/signalementService'

const router = useRouter()

const confidentialCode = ref('')
const isLoading = ref(false)
const error = ref<string | null>(null)

const clearError = () => {
  if (error.value) {
    error.value = null
  }
}

const handleSubmit = async () => {
  // Réinitialiser l'erreur
  error.value = null

  // Validation
  if (!confidentialCode.value.trim()) {
    error.value = 'Veuillez entrer votre code confidentiel'
    return
  }

  isLoading.value = true

  try {
    // Appel API pour récupérer le signalement
    const signalement = await getSignalementByCode(confidentialCode.value)
    
    console.log('Signalement trouvé:', signalement)
    
    // Stocker le signalement dans sessionStorage pour la page de détail
    sessionStorage.setItem('currentSignalement', JSON.stringify(signalement))
    
    // Rediriger vers la page de détail
    router.push({
      name: 'signalement-detail',
      params: { code: confidentialCode.value.trim().toUpperCase() }
    })
    
  } catch (err) {
    console.error('Erreur lors de la récupération du signalement:', err)
    
    const apiError = err as ApiError
    
    // Gestion des erreurs spécifiques selon le code HTTP
    if (apiError.status === 404) {
      error.value = 'Signalement non trouvé. Vérifiez que votre code confidentiel est correct.'
    } else if (apiError.status === 403) {
      error.value = 'Accès refusé. Ce signalement n\'est pas accessible avec ce code.'
    } else if (apiError.status === 401) {
      error.value = 'Authentification requise. Veuillez réessayer.'
    } else if (apiError.status === 422) {
      error.value = 'Code de suivi invalide. Veuillez vérifier le format.'
    } else {
      error.value = apiError.message || 'Une erreur est survenue. Veuillez réessayer plus tard.'
    }
  } finally {
    isLoading.value = false
  }
}
</script>